import { supabase } from '../lib/supabase';

export const authService = {
  async signUp(email, password, fullName, tier = 'free') {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          emailRedirectTo: 'https://boltpay.com/email-verification-portal',
          data: {
            full_name: fullName,
            tier: tier,
            avatar_url: ''
          }
        }
      });
      
      if (error) throw error;

      // Generate fresh verification token for this signup request
      if (data?.user) {
        const { data: tokenData, error: tokenError } = await supabase?.rpc('generate_verification_token');
        if (tokenError) throw tokenError;

        const newToken = tokenData;

        // Update user profile with fresh token
        const { error: updateError } = await supabase
          ?.from('user_profiles')
          ?.update({
            email_verification_token: newToken,
            email_verification_sent_at: new Date()?.toISOString(),
            email_verification_expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000)?.toISOString() // 24 hours
          })
          ?.eq('id', data?.user?.id);

        if (updateError) throw updateError;
      }
      
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Signup failed' } };
    }
  },

  async signIn(email, password) {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Login failed' } };
    }
  },

  async signOut() {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: { message: error?.message || 'Logout failed' } };
    }
  },

  async updateProfile(updates) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case for database
      const dbUpdates = {};
      if (updates?.fullName !== undefined) dbUpdates.full_name = updates?.fullName;
      if (updates?.avatarUrl !== undefined) dbUpdates.avatar_url = updates?.avatarUrl;
      if (updates?.tier !== undefined) dbUpdates.tier = updates?.tier;
      if (updates?.walletAddress !== undefined) dbUpdates.wallet_address = updates?.walletAddress;
      if (updates?.walletStatus !== undefined) dbUpdates.wallet_status = updates?.walletStatus;
      if (updates?.spendingLimit !== undefined) dbUpdates.spending_limit = updates?.spendingLimit;
      if (updates?.monthlySwapLimit !== undefined) dbUpdates.monthly_swap_limit = updates?.monthlySwapLimit;

      const { data, error } = await supabase?.from('user_profiles')?.update(dbUpdates)?.eq('id', user?.id)?.select()?.single();
      
      if (error) throw error;

      // Convert snake_case to camelCase for React
      return {
        data: {
          id: data?.id,
          email: data?.email,
          fullName: data?.full_name,
          avatarUrl: data?.avatar_url,
          tier: data?.tier,
          walletAddress: data?.wallet_address,
          walletStatus: data?.wallet_status,
          spendingLimit: data?.spending_limit,
          monthlySwapLimit: data?.monthly_swap_limit,
          isActive: data?.is_active,
          createdAt: data?.created_at,
          updatedAt: data?.updated_at
        },
        error: null
      };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Update failed' } };
    }
  },

  async getUserProfile() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();
      
      if (error) throw error;

      // Convert snake_case to camelCase
      return {
        data: {
          id: data?.id,
          email: data?.email,
          fullName: data?.full_name,
          avatarUrl: data?.avatar_url,
          tier: data?.tier,
          walletAddress: data?.wallet_address,
          walletStatus: data?.wallet_status,
          spendingLimit: data?.spending_limit,
          monthlySwapLimit: data?.monthly_swap_limit,
          isActive: data?.is_active,
          emailVerified: data?.email_verified,
          emailVerificationToken: data?.email_verification_token,
          createdAt: data?.created_at,
          updatedAt: data?.updated_at
        },
        error: null
      };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to load profile' } };
    }
  },

  async connectWallet(walletAddress) {
    return this.updateProfile({
      walletAddress,
      walletStatus: 'connected'
    });
  },

  async disconnectWallet() {
    return this.updateProfile({
      walletAddress: null,
      walletStatus: 'disconnected'
    });
  },

  async upgradeToPro() {
    // Note: This method is deprecated - use subscriptionService.createCheckoutSession instead
    console.warn('authService.upgradeToPro is deprecated. Use subscriptionService for Stripe payments.');
    return this.updateProfile({
      tier: 'pro',
      spendingLimit: 10000.00,
      monthlySwapLimit: 100
    });
  },

  async resetPassword(email) {
    try {
      // Generate fresh reset token for this request
      const { data: tokenData, error: tokenError } = await supabase?.rpc('generate_password_reset_token');
      if (tokenError) throw tokenError;

      const resetToken = tokenData;
      const resetUrl = `https://boltpay.com/reset-password?token=${resetToken}`;

      // Get user by email
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('id, email, full_name')
        ?.eq('email', email)
        ?.single();

      if (profileError || !profile) {
        // Don't reveal if email exists - return success anyway for security
        return { error: null };
      }

      // Update user profile with fresh reset token
      const { error: updateError } = await supabase
        ?.from('user_profiles')
        ?.update({
          password_reset_token: resetToken,
          password_reset_sent_at: new Date()?.toISOString(),
          password_reset_expires_at: new Date(Date.now() + 60 * 60 * 1000)?.toISOString() // 60 minutes
        })
        ?.eq('id', profile?.id);

      if (updateError) throw updateError;

      // Send custom password reset email with fresh token
      const { error: emailError } = await supabase?.functions?.invoke('send-password-reset-email', {
        body: {
          email,
          resetToken,
          resetUrl
        }
      });

      if (emailError) throw emailError;

      return { error: null };
    } catch (error) {
      return { error: { message: error?.message || 'Failed to send reset email' } };
    }
  },

  async forgotPassword(email) {
    // Redirect to resetPassword method which now handles fresh token generation
    return this.resetPassword(email);
  },

  async updatePassword(newPassword) {
    try {
      const { error } = await supabase?.auth?.updateUser({
        password: newPassword
      });
      
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: { message: error?.message || 'Failed to update password' } };
    }
  },

  // OAuth Provider Sign-In Methods
  async signInWithGoogle() {
    try {
      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location?.origin}/user-wallet-dashboard`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Google sign-in failed' } };
    }
  },

  // Web3 Wallet Authentication Methods
  async signInWithEthereumWallet(walletAddress, signature) {
    try {
      // Verify signature and authenticate with Supabase
      const message = `Sign this message to authenticate with BOLTPay: ${walletAddress}`;
      
      // Use Supabase custom authentication with wallet address
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email: `${walletAddress?.toLowerCase()}@wallet.boltpay.com`,
        password: signature
      });

      if (error) {
        // If user doesn't exist, create account
        if (error?.message?.includes('Invalid login credentials')) {
          return await this.signUpWithEthereumWallet(walletAddress, signature);
        }
        throw error;
      }

      // Update wallet connection status
      await this.connectWallet(walletAddress);

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Ethereum wallet authentication failed' } };
    }
  },

  async signUpWithEthereumWallet(walletAddress, signature) {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email: `${walletAddress?.toLowerCase()}@wallet.boltpay.com`,
        password: signature,
        options: {
          data: {
            full_name: `Ethereum User ${walletAddress?.slice(0, 6)}`,
            tier: 'free',
            wallet_address: walletAddress,
            wallet_type: 'ethereum',
            avatar_url: ''
          }
        }
      });

      if (error) throw error;

      // Mark email as verified for wallet users
      if (data?.user) {
        await supabase
          ?.from('user_profiles')
          ?.update({
            email_verified: true,
            wallet_address: walletAddress,
            wallet_status: 'connected'
          })
          ?.eq('id', data?.user?.id);
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Ethereum wallet signup failed' } };
    }
  },

  async signInWithSolanaWallet(walletAddress, signature, publicKey) {
    try {
      // Use Supabase custom authentication with wallet address
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email: `${walletAddress?.toLowerCase()}@wallet.boltpay.com`,
        password: signature
      });

      if (error) {
        // If user doesn't exist, create account
        if (error?.message?.includes('Invalid login credentials')) {
          return await this.signUpWithSolanaWallet(walletAddress, signature);
        }
        throw error;
      }

      // Update wallet connection status
      await this.connectWallet(walletAddress);

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Solana wallet authentication failed' } };
    }
  },

  async signUpWithSolanaWallet(walletAddress, signature) {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email: `${walletAddress?.toLowerCase()}@wallet.boltpay.com`,
        password: signature,
        options: {
          data: {
            full_name: `Solana User ${walletAddress?.slice(0, 6)}`,
            tier: 'free',
            wallet_address: walletAddress,
            wallet_type: 'solana',
            avatar_url: ''
          }
        }
      });

      if (error) throw error;

      // Mark email as verified for wallet users
      if (data?.user) {
        await supabase
          ?.from('user_profiles')
          ?.update({
            email_verified: true,
            wallet_address: walletAddress,
            wallet_status: 'connected'
          })
          ?.eq('id', data?.user?.id);
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Solana wallet signup failed' } };
    }
  }
};

export default authService;